import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Key, Clock, Calendar as CalendarIcon, User, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const UsersManagement = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role_id: '',
    time_restriction: {
      enabled: false,
      start_time: '09:00',
      end_time: '18:00',
      days: [...DAYS_OF_WEEK]
    }
  });
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/users`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/roles`);
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      full_name: '',
      role_id: roles[0]?.role_id || '',
      time_restriction: {
        enabled: false,
        start_time: '09:00',
        end_time: '18:00',
        days: [...DAYS_OF_WEEK]
      }
    });
    setShowModal(true);
  };

  const handleEdit = (userToEdit) => {
    setEditingUser(userToEdit);
    setFormData({
      username: userToEdit.username,
      email: userToEdit.email,
      password: '', // Don't populate password for edit
      full_name: userToEdit.full_name,
      role_id: userToEdit.role_id,
      time_restriction: userToEdit.time_restriction || {
        enabled: false,
        start_time: '09:00',
        end_time: '18:00',
        days: [...DAYS_OF_WEEK]
      }
    });
    setShowModal(true);
  };

  const handleDayToggle = (day) => {
    const days = [...formData.time_restriction.days];
    const index = days.indexOf(day);
    if (index > -1) {
      days.splice(index, 1);
    } else {
      days.push(day);
    }
    setFormData({
      ...formData,
      time_restriction: { ...formData.time_restriction, days }
    });
  };

  const handleSave = async () => {
    try {
      if (!formData.username.trim()) {
        toast.error('Username is required');
        return;
      }
      if (!editingUser && !formData.password) {
        toast.error('Password is required for new users');
        return;
      }
      if (!formData.role_id) {
        toast.error('Role is required');
        return;
      }

      let response;
      if (editingUser) {
        // Update existing user
        const payload = {
          email: formData.email || `${formData.username}@example.com`,
          full_name: formData.full_name,
          role_id: formData.role_id,
          time_restriction: formData.time_restriction,
          is_active: true
        };
        response = await fetch(`${BACKEND_URL}/api/users/${editingUser.user_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        // Create new user
        const payload = {
          username: formData.username.trim(),
          email: formData.email.trim() || `${formData.username.trim()}@example.com`,
          password: formData.password,
          full_name: formData.full_name.trim() || formData.username.trim(),
          mobile: '0000000000', // Default mobile
          role_id: formData.role_id,
          time_restriction: formData.time_restriction
        };
        response = await fetch(`${BACKEND_URL}/api/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        toast.success(editingUser ? 'User updated successfully' : 'User created successfully');
        setShowModal(false);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save user');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error('Failed to save user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('User deleted successfully');
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleResetPassword = (userToReset) => {
    setPasswordResetUser(userToReset);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handlePasswordResetSave = async () => {
    try {
      if (!newPassword || newPassword.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/users/${passwordResetUser.user_id}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword })
      });

      if (response.ok) {
        toast.success('Password reset successfully');
        setShowPasswordModal(false);
        setPasswordResetUser(null);
        setNewPassword('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password');
    }
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.role_id === roleId);
    return role ? role.role_name : 'Unknown';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">User Management</h2>
          <p className="text-sm text-gray-600">Manage users and access control</p>
        </div>
        <button
          onClick={handleAdd}
          className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100 border-b-2">
              <tr>
                <th className="p-3 text-left">Username</th>
                <th className="p-3 text-left">Full Name</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Password</th>
                <th className="p-3 text-left">Role</th>
                <th className="p-3 text-center">Time Restricted</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((userItem) => (
                <tr key={userItem.user_id} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-mono text-sm">{userItem.username}</td>
                  <td className="p-3">{userItem.full_name}</td>
                  <td className="p-3 text-sm">{userItem.email}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">
                        {visiblePasswords[userItem.user_id]
                          ? (userItem.password_plain || '—')
                          : '••••••••'}
                      </span>
                      <button
                        onClick={() => setVisiblePasswords(prev => ({ ...prev, [userItem.user_id]: !prev[userItem.user_id] }))}
                        className="p-1 hover:bg-gray-100 rounded"
                        title={visiblePasswords[userItem.user_id] ? 'Hide password' : 'View password'}
                      >
                        {visiblePasswords[userItem.user_id]
                          ? <EyeOff className="w-4 h-4 text-gray-500" />
                          : <Eye className="w-4 h-4 text-gray-500" />}
                      </button>
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded text-xs font-semibold">
                      {getRoleName(userItem.role_id)}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    {userItem.time_restriction?.enabled ? (
                      <div className="flex items-center justify-center gap-1 text-xs">
                        <Clock className="w-3 h-3 text-blue-600" />
                        <span>{userItem.time_restriction.start_time} - {userItem.time_restriction.end_time}</span>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      userItem.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {userItem.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => handleEdit(userItem)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Edit User"
                      >
                        <Edit2 className="w-4 h-4 text-blue-600" />
                      </button>
                      <button
                        onClick={() => handleResetPassword(userItem)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Reset Password"
                      >
                        <Key className="w-4 h-4 text-orange-600" />
                      </button>
                      <button
                        onClick={() => handleDelete(userItem.user_id)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Delete User"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-xl font-bold">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h3>
              <button onClick={() => setShowModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Username *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    disabled={!!editingUser}
                    className="w-full border rounded-lg p-2"
                    placeholder="john.doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full border rounded-lg p-2"
                    placeholder="John Doe"
                  />
                </div>
              </div>

              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium mb-1">Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full border rounded-lg p-2"
                    placeholder="Min 6 characters"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Role *</label>
                <select
                  value={formData.role_id}
                  onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Select Role</option>
                  {roles.map((role) => (
                    <option key={role.role_id} value={role.role_id}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Time Restrictions */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    checked={formData.time_restriction.enabled}
                    onChange={(e) => setFormData({
                      ...formData,
                      time_restriction: { ...formData.time_restriction, enabled: e.target.checked }
                    })}
                    className="w-4 h-4"
                  />
                  <label className="font-bold text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Enable Time-Based Login Restrictions
                  </label>
                </div>

                {formData.time_restriction.enabled && (
                  <div className="space-y-4 pl-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Start Time</label>
                        <input
                          type="time"
                          value={formData.time_restriction.start_time}
                          onChange={(e) => setFormData({
                            ...formData,
                            time_restriction: { ...formData.time_restriction, start_time: e.target.value }
                          })}
                          className="w-full border rounded-lg p-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">End Time</label>
                        <input
                          type="time"
                          value={formData.time_restriction.end_time}
                          onChange={(e) => setFormData({
                            ...formData,
                            time_restriction: { ...formData.time_restriction, end_time: e.target.value }
                          })}
                          className="w-full border rounded-lg p-2"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Allowed Days</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {DAYS_OF_WEEK.map((day) => (
                          <label key={day} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.time_restriction.days.includes(day)}
                              onChange={() => handleDayToggle(day)}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{day.slice(0, 3)}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                      <strong>Note:</strong> When time restrictions are enabled and the current time is outside the allowed window, the login modal will not open even with the 10-click mechanism.
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-bold">Reset Password</h3>
              <button onClick={() => setShowPasswordModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Reset password for: <strong>{passwordResetUser?.username}</strong>
                </p>
                <label className="block text-sm font-medium mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border rounded-lg p-2"
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordResetSave}
                className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Key className="w-4 h-4" />
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
